import React, { useState, useEffect, useCallback, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { ScrollArea } from "@/components/ui/scroll-area";
import { toast } from "sonner";
import AdminShell from "@/components/AdminShell";
import { Eyebrow } from "@/components/brand";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Stat = ({ label, value, sub, color = "#0e1b2a" }) => (
  <div className="card-flat p-5">
    <Eyebrow className="mb-2">{label}</Eyebrow>
    <p className="text-4xl font-extrabold tracking-tight tabular-nums" style={{ color }}>{value}</p>
    {sub && <p className="text-mute text-xs mt-1">{sub}</p>}
  </div>
);

export default function AnalyticsPage() {
  const [faqData, setFaqData] = useState(null);
  const [gapsData, setGapsData] = useState(null);
  const [engagementData, setEngagementData] = useState(null);
  const [activeTab, setActiveTab] = useState("faq");
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();
  const token = localStorage.getItem("token");

  const fetchAnalytics = useCallback(async () => {
    try {
      const [faq, gaps, engagement] = await Promise.all([
        axios.get(`${API}/analytics/faq`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/analytics/knowledge-gaps`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/analytics/engagement`, { headers: { Authorization: `Bearer ${token}` } })
      ]);
      setFaqData(faq.data);
      setGapsData(gaps.data);
      setEngagementData(engagement.data);
    } catch { toast.error("Failed to load analytics"); }
    finally { setLoading(false); }
  }, [token]);

  useEffect(() => { fetchAnalytics(); }, [fetchAnalytics]);

  const tabs = [
    { id: "faq", label: "Top Questions" },
    { id: "gaps", label: "Knowledge Gaps" },
    { id: "engagement", label: "Engagement" },
  ];

  // Memoize expensive sort/transform operations
  const sortedTopics = useMemo(() =>
    faqData?.topic_counts
      ? Object.entries(faqData.topic_counts).sort(([,a],[,b]) => b - a)
      : [],
  [faqData]);

  const sortedKeywords = useMemo(() =>
    faqData?.top_keywords
      ? Object.entries(faqData.top_keywords).sort(([,a],[,b]) => b - a).slice(0, 30)
      : [],
  [faqData]);

  const sortedUsers = useMemo(() =>
    engagementData?.user_breakdown
      ? Object.entries(engagementData.user_breakdown)
      : [],
  [engagementData]);

  return (
    <AdminShell active="analytics">
      <div className="flex items-center gap-3 mb-6">
        <button onClick={() => navigate(-1)} className="btn-quiet btn-sm -ml-3" data-testid="back-button">
          <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7"/></svg>
          Back
        </button>
      </div>

      <div className="mb-10">
        <Eyebrow className="mb-3">Program Intelligence</Eyebrow>
        <h1 className="text-4xl sm:text-5xl font-extrabold text-ink tracking-tight leading-[1.05]">Analytics</h1>
        <p className="text-mute mt-4 max-w-xl">How applicants are using Equal and where the knowledge gaps are.</p>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-24">
          <div className="w-8 h-8 border-2 border-line border-t-equal rounded-full animate-spin" />
        </div>
      ) : (
        <div className="max-w-5xl">
          {/* Top stats */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-10">
            <Stat label="Total Users" value={engagementData?.total_users || 0} data-testid="total-users" />
            <Stat label="Conversations" value={engagementData?.total_conversations || 0} color="#0f8a5a" />
            <Stat label="Avg Messages" value={engagementData?.avg_messages_per_conversation || 0} />
            <Stat label="Knowledge Gaps" value={gapsData?.total_gaps || 0} color="#c0392b" sub="unanswered questions" />
          </div>

          {/* Tabs */}
          <div className="flex gap-1 mb-8 border-b border-line pb-0">
            {tabs.map(t => (
              <button key={t.id} onClick={() => setActiveTab(t.id)}
                className={`px-4 py-2.5 text-sm font-medium transition-colors border-b-2 -mb-px ${activeTab === t.id ? "border-equal text-ink" : "border-transparent text-mute hover:text-ink"}`}
                data-testid={`${t.id}-tab`}>
                {t.label}
              </button>
            ))}
          </div>

          {/* FAQ Tab */}
          {activeTab === "faq" && (
            <div className="space-y-6">
              {/* Topic breakdown */}
              {faqData?.topic_counts && Object.keys(faqData.topic_counts).length > 0 && (
                <div className="card-flat p-6">
                  <Eyebrow className="mb-4">Topics</Eyebrow>
                  <div className="space-y-2.5">
                    {sortedTopics
                      .map(([topic, count]) => {
                        const max = Math.max(...sortedTopics.map(([,v]) => v));
                        const pct = Math.round((count / max) * 100);
                        return (
                          <div key={topic} className="flex items-center gap-4" data-testid={`topic-${topic}`}>
                            <span className="text-sm text-ink-2 w-40 flex-shrink-0">{topic}</span>
                            <div className="flex-1 h-2 bg-page rounded-full overflow-hidden border border-line">
                              <div className="h-full rounded-full bg-equal transition-[width]" style={{ width: `${pct}%` }} />
                            </div>
                            <span className="text-sm font-semibold text-ink w-8 text-right tabular-nums font-mono">{count}</span>
                          </div>
                        );
                      })}
                  </div>
                </div>
              )}

              {/* Keywords */}
              {faqData?.top_keywords && Object.keys(faqData.top_keywords).length > 0 && (
                <div className="card-flat p-6">
                  <Eyebrow className="mb-4">Top Keywords</Eyebrow>
                  <div className="flex flex-wrap gap-2">
                    {sortedKeywords
                      .map(([keyword, count]) => (
                        <span key={keyword}
                          className="px-3 py-1 rounded-full text-xs font-medium border border-line bg-white text-ink-2"
                          style={{ fontSize: Math.min(14, 11 + count * 0.5) + 'px' }}
                          data-testid={`keyword-${keyword}`}>
                          {keyword} <span className="text-mute font-mono">({count})</span>
                        </span>
                      ))}
                  </div>
                </div>
              )}

              {/* Sample questions */}
              {faqData?.topic_questions && (
                <div className="card-flat p-6">
                  <Eyebrow className="mb-4">Sample Questions</Eyebrow>
                  <ScrollArea className="h-80">
                    <div className="space-y-5">
                      {Object.entries(faqData.topic_questions).map(([topic, questions]) =>
                        questions.length > 0 && (
                          <div key={topic} data-testid={`sample-questions-${topic}`}>
                            <p className="th-mono mb-2">{topic}</p>
                            <ul className="space-y-1.5">
                              {questions.slice(0, 3).map((q, i) => (
                                <li key={`q-${i}`} className="text-sm text-ink-2 pl-3 border-l-2 border-mint">{q}</li>
                              ))}
                            </ul>
                          </div>
                        )
                      )}
                    </div>
                  </ScrollArea>
                </div>
              )}

              {(!faqData?.topic_counts || Object.keys(faqData.topic_counts).length === 0) && (
                <div className="card-flat text-center py-16">
                  <p className="text-ink text-sm font-medium">No questions analyzed yet.</p>
                  <p className="text-mute text-xs mt-1">Data appears once clients start using the chat.</p>
                </div>
              )}
            </div>
          )}

          {/* Knowledge Gaps Tab */}
          {activeTab === "gaps" && (
            <div>
              {!gapsData?.gaps || gapsData.gaps.length === 0 ? (
                <div className="card-flat text-center py-16" data-testid="no-gaps-message">
                  <div className="w-10 h-10 rounded-full bg-mint border border-[#bfe5d1] flex items-center justify-center mx-auto mb-3">
                    <svg className="w-5 h-5 text-equal-dark" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7"/>
                    </svg>
                  </div>
                  <p className="text-ink text-sm font-medium">No knowledge gaps identified</p>
                  <p className="text-mute text-xs mt-1">The AI is answering all questions from the knowledge base.</p>
                </div>
              ) : (
                <ScrollArea className="h-[500px]">
                  <div className="space-y-3">
                    {gapsData.gaps.map((gap, idx) => (
                      <div key={`gap-${idx}`} className="card-flat p-4 hover:bg-page transition-colors" data-testid={`gap-${idx}`}>
                        <div className="flex items-start justify-between gap-3">
                          <div className="flex-1">
                            <p className="text-sm font-medium text-ink mb-1">{gap.question}</p>
                            <p className="text-xs text-mute leading-relaxed line-clamp-2">{gap.response}</p>
                            <p className="text-xs text-mute/70 mt-2 font-mono">{new Date(gap.timestamp).toLocaleString()}</p>
                          </div>
                          {gap.flagged && (
                            <span className="flex-shrink-0 text-xs px-2 py-0.5 rounded-md bg-warm text-[#c47a12] border border-[#f3d9b0]">Flagged</span>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </ScrollArea>
              )}
            </div>
          )}

          {/* Engagement Tab */}
          {activeTab === "engagement" && (
            <div className="space-y-6">
              {!engagementData?.user_breakdown || Object.keys(engagementData.user_breakdown).length === 0 ? (
                <div className="card-flat text-center py-16" data-testid="no-user-data-message">
                  <p className="text-mute text-sm">No user activity yet.</p>
                </div>
              ) : (
                <div>
                  <Eyebrow className="mb-4">Per-User Activity</Eyebrow>
                  <div className="card-flat overflow-hidden">
                    <div className="grid grid-cols-4 px-4 py-2.5 th-mono border-b border-line">
                      <span>User</span>
                      <span className="text-center">Sessions</span>
                      <span className="text-center">Messages</span>
                      <span className="text-center">Flagged</span>
                    </div>
                    <ScrollArea className="h-80">
                      <div className="divide-y divide-line">
                        {sortedUsers.map(([userEmail, stats]) => (
                          <div key={userEmail} className="grid grid-cols-4 px-4 py-3 hover:bg-page transition-colors" data-testid={`user-stats-${userEmail}`}>
                            <span className="text-sm text-ink-2 truncate">{userEmail}</span>
                            <span className="text-sm font-semibold text-ink text-center tabular-nums">{stats.conversations}</span>
                            <span className="text-sm font-semibold text-ink text-center tabular-nums">{stats.messages}</span>
                            <span className={`text-sm font-semibold text-center tabular-nums ${stats.flagged_count > 0 ? "text-[#c47a12]" : "text-mute/50"}`}>{stats.flagged_count}</span>
                          </div>
                        ))}
                      </div>
                    </ScrollArea>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </AdminShell>
  );
}
